import { IConfiguredProductData } from 'pages/ConfigurePage/ConfigureForm/hooks';
import i18n, { FALLBACK_LOCALE } from 'providers/i18n/i18n';
import { IQueryResponseDTO, queryModel } from 'services/ModelService';
import { parseQuestionsObjectResponseIntoArray } from 'store/Model/reducers/model';
import { IConfigurationQuestion } from 'types/ConfigurationQuestion.types';
import { IQuestion, QuestionType } from 'types/Question.types';

export interface IQuestionDifferences {
  missingQuestionsKeys: string[];
  extraQuestionsKeys: string[];
  matchingQuestionsWithInvalidOutputsKeys: string[];
}

export const checkQuestionDifferences = async (
  editConfigurationData: IConfiguredProductData
): Promise<{
  differences: IQuestionDifferences;
  extraPriceQuestions: IConfigurationQuestion[] | null;
}> => {
  if (!editConfigurationData || !editConfigurationData.modelId) {
    return {
      differences: {
        missingQuestionsKeys: [],
        extraQuestionsKeys: [],
        matchingQuestionsWithInvalidOutputsKeys: [],
      },
      extraPriceQuestions: null,
    };
  }

  // Query with the same language as initial configuration to avoid raising validation errors
  const lang = editConfigurationData.lang || FALLBACK_LOCALE;
  i18n.changeLanguage(lang);
  const queryModelPromise = queryModel(
    editConfigurationData.modelId.toString(),
    { questions: editConfigurationData.queryPayload } || {},
    lang
  );
  let queryData: IQueryResponseDTO;
  try {
    [queryData] = await Promise.all([queryModelPromise]);
  } catch (error: any) {
    return {
      differences: checkQuestionsMismatchWhenQueryError(
        error?.response?.data?.errors || []
      ),
      extraPriceQuestions: null,
    };
  }
  return {
    differences: checkQuestionsMismatch(
      editConfigurationData.questions,
      parseQuestionsObjectResponseIntoArray(queryData.questions)
    ),
    extraPriceQuestions: queryData?.extra_price_questions || null,
  };
};

interface IQueryQuestionKeyError {
  id: string;
  message: string;
  position: string;
}
const checkQuestionsMismatchWhenQueryError = (
  errors: IQueryQuestionKeyError[]
): IQuestionDifferences => {
  /* Might require handling of additional backend error types */
  const extractQuestionKeyFromErrorMsg = (text: string) => {
    // Extract text between quotes after "question key"
    const regex = /question key "([^"]+)"/;
    const match = text.match(regex);
    return match ? match[1] : '';
  };
  return {
    missingQuestionsKeys: [],
    extraQuestionsKeys: [],
    matchingQuestionsWithInvalidOutputsKeys: errors.map(
      (error: IQueryQuestionKeyError) =>
        extractQuestionKeyFromErrorMsg(error.position)
    ),
  };
};

const checkQuestionsMismatch = (
  editQuestions: IQuestion[],
  currentQuestions: IQuestion[]
): IQuestionDifferences => {
  const missingQuestions: IQuestion[] = [];
  const extraQuestions: IQuestion[] = [];
  const matchingQuestionsWithInvalidOutputs: IQuestion[] = [];

  // Find missing questions
  for (const editQuestion of editQuestions) {
    let currentQuestionThatMatchesEditQuestion: IQuestion | null = null;
    for (const currentQuestion of currentQuestions) {
      if (currentQuestion.initialKey === editQuestion.initialKey) {
        currentQuestionThatMatchesEditQuestion = currentQuestion;
      }
    }
    if (!currentQuestionThatMatchesEditQuestion) {
      missingQuestions.push(editQuestion);
    } else {
      // This question exists in both editQuestions and currentQuestions,
      // check if edit question output exists as a valid input
      if (
        !isEditQuestionOutputValid(
          editQuestion,
          currentQuestionThatMatchesEditQuestion
        )
      ) {
        matchingQuestionsWithInvalidOutputs.push(
          currentQuestionThatMatchesEditQuestion
        );
      }
    }
  }

  // Find extra questions
  for (const currentQuestion of currentQuestions) {
    let currentQuestionFound = false;
    for (const editQuestion of editQuestions) {
      if (currentQuestion.initialKey === editQuestion.initialKey) {
        currentQuestionFound = true;
      }
    }
    if (!currentQuestionFound) {
      extraQuestions.push(currentQuestion);
    }
  }

  return {
    missingQuestionsKeys: missingQuestions.map(
      (question: IQuestion) => question.initialKey
    ),
    extraQuestionsKeys: extraQuestions.map(
      (question: IQuestion) => question.initialKey
    ),
    matchingQuestionsWithInvalidOutputsKeys:
      matchingQuestionsWithInvalidOutputs.map(
        (question: IQuestion) => question.initialKey
      ),
  };
};

const isEditQuestionOutputValid = (
  editQuestion: IQuestion,
  currentQuestion: IQuestion
) => {
  switch (editQuestion.type) {
    case QuestionType.LIST:
      return isListQuestionOutputValid(editQuestion, currentQuestion);
    case QuestionType.TABLE:
      return isTableQuestionOutputValid(editQuestion, currentQuestion);
    default:
      return true;
  }
};

const isListQuestionOutputValid = (
  editQuestion: IQuestion,
  currentQuestion: IQuestion
) => {
  // Convert the outputs object into an array, based on the order of keys in 'inputs'
  const outputsArray = currentQuestion.inputs.map(
    (input) => editQuestion.outputs[input]
  );

  // Check if the filteredOutputsArray exists as an option in currentQuestion.options
  return currentQuestion.options.some((option: any) => {
    return option[0] === outputsArray[0];
  });
};

const isTableQuestionOutputValid = (
  editQuestion: IQuestion,
  currentQuestion: IQuestion
) => {
  // Convert the outputs object into an array, based on the order of keys in 'inputs'
  const rawSelected = currentQuestion.inputs.map(
    (input) => editQuestion.outputs[input]
  )[0];

  const selectedRows = Array.isArray(rawSelected)
    ? rawSelected
    : rawSelected !== undefined && rawSelected !== null
    ? [rawSelected]
    : [];

  const rawEditValues = currentQuestion.inputs.map(
    (input) => editQuestion.outputs[input]
  )[0];

  const rawCurrentValues = currentQuestion.inputs.map(
    (input) => currentQuestion.outputs[input]
  )[0];

  const editValuesEmpty =
    !rawEditValues ||
    (Array.isArray(rawEditValues) && rawEditValues.length === 0);
  const currentValuesEmpty =
    !rawCurrentValues ||
    (Array.isArray(rawCurrentValues) && rawCurrentValues.length === 0);

  // If both editValues and currentValues are empty, return true
  // This means that the question is not set and should be considered valid
  if (editValuesEmpty && currentValuesEmpty) {
    return true;
  }

  // Check if the filteredOutputsArray exists as an option in currentQuestion.options
  const areAllSelectedRowsValid = selectedRows.every((selectedRow: any) => {
    return currentQuestion.options.some((option: any) => {
      const match = option[0] === selectedRow;
      return match;
    });
  });

  return areAllSelectedRowsValid;
};
